// =============================================================================
// VandieZ admin-web -- Live trips view (/trips/live)
// =============================================================================
// Ops surface: lists every trip in an in-flight status with the parent
// lane, driver + shipper identity, vehicle plate, money, and the latest
// GPS sample.  Auto-refreshes every 30 seconds so the support team has
// a near-live picture without leaning on browser tabs.  Read-only.
//
// Single round-trip backed by GET /admin/trips/live; the backend joins
// loads + users + vehicles so the row tile carries everything it
// needs.  Mirrors the WalletAuditPage layout (loader + table + pager).
// =============================================================================

import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Download,
  RefreshCw,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { streamCsvDownload, tripsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminLiveTripStatus,
  AdminTripLiveRead,
} from "@/types/admin_trips";

const PAGE = 50;

/// Filter chips at the top of the page.  `value === null` means "all
/// in-flight statuses" — the backend default; everything else is a
/// comma-separated subset of the seven in-flight statuses.
const STATUS_FILTERS: {
  label: string;
  value: AdminLiveTripStatus[] | null;
}[] = [
  { label: "All", value: null },
  { label: "Booked", value: ["booked"] },
  {
    label: "En route",
    value: ["en_route_pickup", "in_transit"],
  },
  {
    label: "At location",
    value: ["arrived_pickup", "arrived_drop"],
  },
  {
    label: "Loading / Unloading",
    value: ["loading", "unloading"],
  },
];

// Poll interval for the auto-refresh.  30 s is the sweet spot for ops
// surfaces: quick enough that the page feels alive, slow enough that
// the support team can read a row without it flickering away mid-scan.
const REFRESH_MS = 30_000;

function shortId(id: string): string {
  return id.slice(0, 8);
}

/// Title-cases a snake_case district wire value: `coimbatore_north` ->
/// `Coimbatore North`.
function titleCase(s: string | null): string | null {
  if (s === null || s === "") {
    return null;
  }
  return s
    .split("_")
    .map((w) =>
      w.length === 0 ? w : (w.charAt(0).toUpperCase() + w.slice(1)),
    )
    .join(" ");
}

function laneLabel(t: AdminTripLiveRead): string {
  const pickup = titleCase(t.pickup_district);
  const drop = titleCase(t.drop_district);
  if (pickup !== null && drop !== null) {
    return `${pickup} → ${drop}`;
  }
  return `Trip ${shortId(t.id)}`;
}

/// Human label for the wire-value status.  Kept compact for the table
/// column ("In transit" rather than the full "In transit to drop").
function statusLabel(s: AdminLiveTripStatus): string {
  switch (s) {
    case "booked":
      return "Booked";
    case "en_route_pickup":
      return "En route to pickup";
    case "arrived_pickup":
      return "At pickup";
    case "loading":
      return "Loading";
    case "in_transit":
      return "In transit";
    case "arrived_drop":
      return "At drop";
    case "unloading":
      return "Unloading";
  }
}

/// Tailwind classes for the status pill.  Hot statuses (in_transit) get
/// emerald accent; everything else stays neutral.
function statusPillClasses(s: AdminLiveTripStatus): string {
  switch (s) {
    case "in_transit":
    case "en_route_pickup":
      return "bg-success-subtle text-success-subtle-foreground";
    case "loading":
    case "unloading":
      return "bg-warning-subtle text-warning-subtle-foreground";
    case "arrived_pickup":
    case "arrived_drop":
      return "bg-primary-subtle text-primary-subtle-foreground";
    case "booked":
      return "bg-muted text-muted-foreground";
  }
}

/// "5 mins ago" / "2 hours ago" / "—" — used for the freshness column
/// so support can spot a stale GPS sample at a glance.
function freshness(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) {
    return "just now";
  }
  if (diffMin < 60) {
    return `${diffMin} min ago`;
  }
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) {
    return `${diffH} h ago`;
  }
  return `${Math.round(diffH / 24)} d ago`;
}

export function TripsLivePage(): React.JSX.Element {
  const { accessToken, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminTripLiveRead[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  // Index into STATUS_FILTERS — null `value` means "every in-flight".
  const [filterIndex, setFilterIndex] = useState<number>(0);

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      try {
        const filterValue = STATUS_FILTERS[filterIndex]?.value ?? null;
        const statusParam =
          filterValue === null ? undefined : filterValue.join(",");
        const res = await tripsAdminApi.listLive(
          accessToken,
          {
            limit: PAGE,
            offset: nextOffset,
            ...(statusParam !== undefined ? { status: statusParam } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setLastFetchedAt(new Date());
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        if (e instanceof ApiError && e.problem.status === 401) {
          void logout();
          navigate("/login");
        }
      } finally {
        if (signal?.aborted !== true) {
          setLoading(false);
        }
      }
    },
    [accessToken, logout, navigate, filterIndex],
  );

  // Initial fetch + reset offset when the component mounts or when the
  // filter changes.  Reset to page 0 on every filter change so we never
  // page past a now-smaller filtered total.
  useEffect(() => {
    const controller = new AbortController();
    setOffset(0);
    void fetchPage(0, controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchPage]);

  // Auto-refresh every REFRESH_MS while the page is mounted.  Keeps
  // the current offset so a support engineer reading page 2 doesn't
  // get bounced back to page 1.
  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const id = setInterval(() => {
      void fetchPage(offset);
    }, REFRESH_MS);
    return () => {
      clearInterval(id);
    };
  }, [accessToken, fetchPage, offset]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE));
  const currentPage = Math.floor(offset / PAGE) + 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live Trips"
        description="Every trip currently in flight (booked → unloading). Auto-refreshes every 30 seconds."
        actions={
          <>
            {/* p108: "Admin should export load data for operations and
                analysis." `/admin/exports/trips.csv` shipped with the
                other five exports and was the only one no page ever
                offered -- found by re-running the headless sweep. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (accessToken === null) {
                  return;
                }
                void streamCsvDownload(
                  accessToken,
                  "/admin/exports/trips.csv",
                  `vandiez-trips-${new Date().toISOString().slice(0, 10)}.csv`,
                );
              }}
            >
              <Download className="mr-1 h-4 w-4" /> CSV
            </Button>
            {lastFetchedAt !== null && (
              <span className="text-xs text-muted-foreground">
                Last refresh {lastFetchedAt.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => {
                void fetchPage(offset);
              }}
            >
              <RefreshCw
                className={cn("h-4 w-4", loading && "animate-spin")}
              />
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f, i) => (
          <button
            key={f.label}
            type="button"
            onClick={() => {
              setFilterIndex(i);
            }}
            className={cn(
              "rounded-full px-3 py-1 text-sm font-medium",
              filterIndex === i
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {total} live trip{total === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No trips are in flight right now.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-2 text-left">Lane</th>
                    <th className="pb-2 text-left">Status</th>
                    <th className="pb-2 text-left">Driver</th>
                    <th className="pb-2 text-left">Shipper</th>
                    <th className="pb-2 text-left">Vehicle</th>
                    <th className="pb-2 text-left">Trip value</th>
                    <th className="pb-2 text-left">GPS</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b last:border-0 cursor-pointer hover:bg-muted/40"
                      onClick={() => { navigate(`/trips/${t.id}`); }}
                    >
                      <td className="py-2">
                        <div className="font-medium">{laneLabel(t)}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {shortId(t.id)}
                        </div>
                      </td>
                      <td className="py-2">
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-semibold",
                            statusPillClasses(t.status),
                          )}
                        >
                          {statusLabel(t.status)}
                        </span>
                      </td>
                      <td className="py-2">
                        {t.driver_full_name ?? (
                          <span className="font-mono text-xs">
                            {shortId(t.driver_id)}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {t.shipper_full_name ?? (
                          <span className="font-mono text-xs">
                            {shortId(t.shipper_id)}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {t.vehicle_registration_number ?? (
                          <span className="font-mono text-xs">
                            {shortId(t.vehicle_id)}
                          </span>
                        )}
                      </td>
                      <td className="py-2">₹{t.trip_value}</td>
                      <td className="py-2 text-muted-foreground">
                        {t.gps_recorded_at === null
                          ? "no ping"
                          : freshness(t.gps_recorded_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => {
                    const p = Math.max(0, offset - PAGE);
                    setOffset(p);
                    void fetchPage(p);
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE >= total}
                  onClick={() => {
                    const n = offset + PAGE;
                    setOffset(n);
                    void fetchPage(n);
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
